import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { IconEdit, IconEye, IconSeal } from '../../../common/icons';
import { Gate } from '../../../global/contexts/auth/auth';
import { useProjectName } from '../../../global/contexts/lookups/lookups';
import { useProjectsList } from '../../projects/contexts/projects';
import { AgreeTierButton } from '../../breakdown/layouts/agree-tier';
import { TIERS_PAGE_SIZE, useTierProgress, useTiersList } from '../contexts/tiers';
import DeactivateButton from '../../../common/components/deactivate/deactivate-button';
import { tierKeys } from '../queries/keys';
import { tierPaths } from '../queries/paths';
import { COMPLETION_FILTERS, matchesCompletion } from '../utils/progress';
import { BREAKDOWN_STATE_LABEL, TIER_PHASE_LABEL, TIER_STATE_OPTIONS } from '../utils/form';

import '../styles/overview.css';

// Rows, not cards: tiers are an ORDER. They are read down the page — this one
// first, that one after — and `sort_order` is the column that says so. Cards
// would scatter a sequence into a grid.

const TIER_STATE_LABEL = Object.fromEntries(
    TIER_STATE_OPTIONS.map(option => [option.value, option.label]),
);

const PROJECT_LOOKUP_LIMIT = 100;

// THE AGREEMENT, NEXT TO THE STATE AND NOT INSTEAD OF IT. `tier_state` says
// whether the tier is being worked; `breakdown_state` says whether its scope is
// agreed — and only an agreed tier takes task proposals. Two columns, because
// one "Estado" would hide the one that blocks the work.
//
// ACORDAR OPENS A MODAL, IN PLACE. Agreeing sends the `breakdown_revision`
// that was READ and is refused with the list of what is missing, so the modal
// reads the breakdown when it opens and shows both before anything is signed —
// without taking the person away from the list. «Desglose» stays for everybody
// who can read it and not agree it.
function Agreement({ tier }) {
    const draft = tier.breakdown_state === 'draft';
    const to = `/admin/tiers/${tier.id}/desglose`;

    const toBreakdown = (
        <Gate domain="breakdown:read">
            <Link to={to} className="tier-row__link">Desglose</Link>
        </Gate>
    );

    if (!draft) return toBreakdown;

    return (
        <Gate domain="tiers:agree" fallback={toBreakdown}>
            <AgreeTierButton tierId={tier.id} />
        </Gate>
    );
}

// HOW FAR THE TIER GOT: done over what counts, and «Completado» when there is
// nothing left. Anything unfinished is the one that gets the colour — a list of
// tiers is read to find what is still open, so that is what has to stand out.
function Progress({ progress }) {
    if (!progress?.known) return <span className="tier-row__progress-empty">…</span>;

    if (progress.counted === 0) {
        // Nothing authorized: either the tier is empty, or everything it has is
        // still a proposal waiting for a person.
        return progress.proposed > 0
            ? (
                <span
                    className="tier-row__progress-empty"
                    title={`${progress.proposed} propuesta${progress.proposed === 1 ? '' : 's'} sin autorizar, y nada autorizado todavía`}
                >
                    Sin autorizar <span className="tier-row__proposed">+{progress.proposed}</span>
                </span>
            )
            : <span className="tier-row__progress-empty" title="El tier no tiene tareas que contar">Sin tareas</span>;
    }

    if (progress.complete) {
        return (
            <span
                className="tier-row__progress tier-row__progress--done"
                title={progress.proposed > 0
                    ? `${progress.done} de ${progress.counted} tareas autorizadas hechas · ${progress.proposed} propuesta${progress.proposed === 1 ? '' : 's'} sin autorizar`
                    : `${progress.done} de ${progress.counted} tareas autorizadas hechas`}
            >
                <IconSeal />
                Completado
                {progress.proposed > 0 && <span className="tier-row__proposed">+{progress.proposed}</span>}
            </span>
        );
    }

    return (
        <span
            className="tier-row__progress tier-row__progress--open"
            title={progress.proposed > 0
                ? `${progress.done} de ${progress.counted} tareas autorizadas hechas · ${progress.proposed} propuesta${progress.proposed === 1 ? '' : 's'} sin autorizar`
                : `${progress.done} de ${progress.counted} tareas autorizadas hechas`}
        >
            <span className="tier-row__bar" aria-hidden="true">
                <span className="tier-row__bar-fill" style={{ width: `${Math.round(progress.ratio * 100)}%` }} />
            </span>
            {progress.done}/{progress.counted}
            {progress.proposed > 0 && <span className="tier-row__proposed">+{progress.proposed}</span>}
        </span>
    );
}

function TierRow({ tier, projectName, progress }) {
    return (
        <li className={`tier-row${progress?.complete ? ' tier-row--complete' : ''}`}>
            <span className="tier-row__order" aria-hidden="true">{tier.sort_order ?? 0}</span>

            <span className="tier-row__identity">
                {/* The name opens the tier: reading one is `tiers:read`, which
                    is what this list already asks for. */}
                <Link to={`/admin/tiers/${tier.id}`} className="tier-row__name">{tier.name}</Link>
                <span className="tier-row__project">{projectName(tier.project_id)}</span>
            </span>

            <span className="tier-row__phase">
                {TIER_PHASE_LABEL[tier.phase] ?? tier.phase ?? '—'}
            </span>

            <span className={`tier-row__state tier-row__state--${tier.tier_state}`}>
                {TIER_STATE_LABEL[tier.tier_state] ?? tier.tier_state}
            </span>

            <span className="tier-row__progress-cell">
                <Progress progress={progress} />
            </span>

            <span className="tier-row__agreement">
                <span
                    className={`tier-row__breakdown tier-row__breakdown--${tier.breakdown_state ?? 'unknown'}`}
                    title="Preparación del desglose"
                >
                    {BREAKDOWN_STATE_LABEL[tier.breakdown_state] ?? tier.breakdown_state ?? '—'}
                </span>
                <Agreement tier={tier} />
            </span>

            <span className="tier-row__tasks">
                {/* Straight to the LIST, with the project: only the list reads
                    `?tier=`, and the section opens on the board, which would
                    show every task under a link that promised this tier's. */}
                <Link
                    to={`/admin/development/tareas?view=list&project=${tier.project_id}&tier=${tier.id}`}
                    className="tier-row__link"
                >
                    Ver tareas
                </Link>
            </span>

            {/* Two doors, two permissions. Seeing a tier was hidden behind the
                edit link, so without `tiers:write` there was no way in at all. */}
            <span className="tier-row__icons">
                <Link
                    to={`/admin/tiers/${tier.id}`}
                    className="tier-row__icon"
                    aria-label={`Ver ${tier.name}`}
                    title="Ver"
                >
                    <IconEye />
                </Link>
                <Gate domain="tiers:write">
                    <Link
                        to={`/admin/tiers/${tier.id}/edit`}
                        className="tier-row__icon"
                        aria-label={`Editar ${tier.name}`}
                        title="Editar"
                    >
                        <IconEdit />
                    </Link>
                </Gate>
                <Gate domain="tiers:admin">
                    <DeactivateButton
                        name={tier.name}
                        noun="tier"
                        path={tierPaths.detail(tier.id)}
                        invalidate={[tierKeys.all]}
                        className="tier-row__icon"
                    />
                </Gate>
            </span>
        </li>
    );
}

export default function TiersOverview() {
    const [params, setParams] = useSearchParams();

    const page = Math.max(Number(params.get('page')) || 1, 1);
    const projectId = params.get('project') || '';
    const tierState = params.get('state') || '';
    const offset = (page - 1) * TIERS_PAGE_SIZE;

    const { data, isLoading, isFetching, error } = useTiersList({
        limit: TIERS_PAGE_SIZE,
        offset,
        projectId,
        tierState,
    });

    // The filter still lists projects from the page query; the NAME in each row
    // comes from the shared catalog, the same resolver the task screens use.
    const { data: projectPage } = useProjectsList({ limit: PROJECT_LOOKUP_LIMIT, offset: 0 });
    const projectName = useProjectName();

    // The progress of the tiers ON THIS PAGE. Ids as a stable list, so the
    // counts are not re-asked on every render.
    const tierIds = useMemo(() => (data?.items ?? []).map(tier => tier.id), [data]);
    const progressByTier = useTierProgress(tierIds);
    const completion = params.get('completados') || '';

    const rows = data?.items ?? [];
    const items = rows.filter(tier => matchesCompletion(progressByTier.get(String(tier.id)), completion));
    const hidden = rows.length - items.length;
    const total = data?.pagination?.total ?? 0;
    const lastPage = useMemo(
        () => Math.max(Math.ceil(total / TIERS_PAGE_SIZE), 1),
        [total],
    );

    const setFilter = (key, value) => {
        const target = new URLSearchParams(params);
        if (value) target.set(key, value);
        else target.delete(key);
        target.delete('page');
        if (key === 'project') { target.delete('repo'); target.delete('tier'); }
        setParams(target);
    };

    const goToPage = (next) => {
        const target = new URLSearchParams(params);
        if (next <= 1) target.delete('page');
        else target.set('page', String(next));
        setParams(target);
    };

    const hasFilters = !!(projectId || tierState || completion);

    if (isLoading) return <p className="tiers__loading">Cargando tiers...</p>;

    if (error) {
        return (
            <p className="tiers__error">
                {error.message || 'No se pudieron cargar los tiers'}
            </p>
        );
    }

    return (
        <section className="tiers">
            <header className="tiers__header">
                <div className="tiers__heading">
                    <span className="tiers__count">{total} {total === 1 ? 'tier' : 'tiers'}</span>
                    <h2 className="tiers__title">Tiers</h2>
                </div>

                <Gate domain="tiers:write">
                    {/* Carries the project filter along: creating a tier while
                        looking at one project should not ask which project. */}
                    <Link
                        to={`/admin/tiers/new${projectId ? `?project=${projectId}` : ''}`}
                        className="tiers__new"
                    >
                        + Nuevo tier
                    </Link>
                </Gate>
            </header>

            <div className="tiers__filters">
                <select
                    className="tiers__filter"
                    value={projectId}
                    onChange={e => setFilter('project', e.target.value)}
                    aria-label="Filtrar por proyecto"
                >
                    <option value="">Todos los proyectos</option>
                    {(projectPage?.items ?? []).map(project => (
                        <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                </select>

                <select
                    className="tiers__filter"
                    value={tierState}
                    onChange={e => setFilter('state', e.target.value)}
                    aria-label="Filtrar por estado"
                >
                    <option value="">Todos los estados</option>
                    {TIER_STATE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>

                <div className="tiers__completion" role="group" aria-label="Filtrar por cumplimiento">
                    {COMPLETION_FILTERS.map(option => (
                        <button
                            key={option.key || 'todos'}
                            type="button"
                            className={`tiers__completion-option${completion === option.key ? ' tiers__completion-option--on' : ''}`}
                            onClick={() => setFilter('completados', option.key)}
                            aria-pressed={completion === option.key}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>

                {hasFilters && (
                    <button
                        type="button"
                        className="tiers__filter-clear"
                        onClick={() => setParams(new URLSearchParams())}
                    >
                        Limpiar
                    </button>
                )}
            </div>

            {!items.length && (
                <p className="tiers__empty">
                    {hasFilters
                        ? 'Ningún tier coincide con estos filtros.'
                        : 'Todavía no hay tiers cargados.'}
                </p>
            )}

            {/* The completion filter runs over the page that was fetched, not
                over the server: `GET /admin/tiers` does not filter by progress,
                because Core does not keep it. Said out loud, so a page that
                shows three of twenty-five does not read as a bug. */}
            {hidden > 0 && (
                <p className="tiers__note">
                    Se {hidden === 1 ? 'oculta' : 'ocultan'} {hidden} de esta página por el filtro
                    de cumplimiento. El filtro se aplica sobre la página cargada.
                </p>
            )}

            {!!items.length && (
                <div className={`tiers__list ${isFetching ? 'tiers__list--refreshing' : ''}`}>
                    <div className="tier-row tier-row--head" aria-hidden="true">
                        <span>#</span>
                        <span>Tier</span>
                        <span>Fase</span>
                        <span>Estado</span>
                        <span>Avance</span>
                        <span>Acuerdo</span>
                        <span />
                        <span />
                    </div>

                    <ul className="tiers__rows">
                        {items.map(tier => (
                            <TierRow
                                key={tier.id}
                                tier={tier}
                                projectName={projectName}
                                progress={progressByTier.get(String(tier.id))}
                            />
                        ))}
                    </ul>
                </div>
            )}

            {total > TIERS_PAGE_SIZE && (
                <nav className="tiers__pager">
                    <button
                        type="button"
                        className="tiers__pager-button"
                        onClick={() => goToPage(page - 1)}
                        disabled={page <= 1}
                    >
                        Anterior
                    </button>
                    <span className="tiers__pager-position">Página {page} de {lastPage}</span>
                    <button
                        type="button"
                        className="tiers__pager-button"
                        onClick={() => goToPage(page + 1)}
                        disabled={page >= lastPage}
                    >
                        Siguiente
                    </button>
                </nav>
            )}
        </section>
    );
}
